import { apiClient } from './client';
import type { BaseUser, UserRole, ProfileName } from '@comp-sys/shared';
import type { AuditLogsResponse, QueryAuditLogsParams } from './system';

// Dashboard stats types
export interface DashboardStats {
  role: string;
  // Admin stats
  totalUsers?: number;
  pendingProfiles?: number;
  totalCompetitions?: number;
  totalEvents?: number;
  // School stats
  schoolProfiles?: number;
  schoolTeams?: number;
  schoolAwards?: number;
  // Judge stats
  judgedCompetitions?: number;
  pendingScores?: number;
  // Contestant/Coach/Volunteer stats
  myTeams?: number;
  activeCompetitions?: number;
  // Competition Manager stats
  myCompetitions?: number;
  activeManagedCompetitions?: number;
  totalTeams?: number;
  pendingItems?: number;
  todoItems?: TodoItem[];
}

export interface TodoItem {
  type: 'PENDING_TEAMS' | 'UPCOMING_START' | 'NEEDS_AWARDS';
  title: string;
  description: string;
  competitionId: string;
  eventId: string;
}

// Users API types
export interface UsersListParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: UserRole;
  status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  schoolId?: string;
}

export interface CreateUserRequest {
  phoneNumber?: string;
  countryCode?: string;
  email?: string;
  displayName?: string;
  password: string;
  /** Multi-role assignment (Task 7+). Sent alongside legacy `role` for back-compat. */
  roles?: UserRole[];
  /** Legacy singular role — derived from roles[0] when roles is provided. */
  role?: UserRole;
  isActive?: boolean;
  locale?: string;
  schoolId?: string;
  institutionId?: string;
}

export interface UpdateUserRequest {
  phoneNumber?: string;
  countryCode?: string;
  email?: string;
  displayName?: string;
  /** Multi-role assignment (Task 7+). Sent alongside legacy `role` for back-compat. */
  roles?: UserRole[];
  /** Legacy singular role — derived from roles[0] when roles is provided. */
  role?: UserRole;
  isActive?: boolean;
  locale?: string;
  schoolId?: string | null;
  institutionId?: string | null;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
  encrypted?: boolean;
}

export interface AdminResetPasswordRequest {
  adminPassword: string;
  newPassword: string;
  encrypted?: boolean;
}

export interface UsersListMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface EnrollmentSummary {
  competition: { id: string; name: string };
  group: { id: string; name: string; code: string } | null;
}

export interface ProfileSummary {
  id: string;
  names: { lang: string; value: string }[] | Record<string, string>;
  certNo: string | null;
  idType: string | null;
  idNo: string | null;
  schoolName: string | null;
  status: string;
  school?: { id: string; name: string } | null;
  enrollments: EnrollmentSummary[];
}

export interface UserWithProfiles {
  id: string;
  phoneNumber: string | null;
  countryCode: string | null;
  email: string | null;
  displayName: string | null;
  role: string;
  locale: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  schoolId: string | null;
  institutionId: string | null;
  school?: { id: string; name: string } | null;
  profiles?: ProfileSummary[];
}

export interface UsersListResponse {
  data: UserWithProfiles[];
  meta: UsersListMeta;
}

// Users API functions
export const usersApi = {
  getStats: () =>
    apiClient.get<DashboardStats>('users/stats'),

  list: (params?: UsersListParams) => {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) {
      searchParams.set('page', String(params.page));
    }
    if (params?.limit !== undefined) {
      searchParams.set('limit', String(params.limit));
    }
    if (params?.search !== undefined) {
      searchParams.set('search', params.search);
    }
    if (params?.role !== undefined) {
      searchParams.set('role', params.role);
    }
    if (params?.status !== undefined) {
      searchParams.set('status', params.status);
    }
    if (params?.schoolId !== undefined) {
      searchParams.set('schoolId', params.schoolId);
    }
    const queryString = searchParams.toString();
    const endpoint = queryString ? `users?${queryString}` : 'users';
    return apiClient.get<UsersListResponse>(endpoint, { cache: 'no-store' });
  },

  get: (id: string) =>
    apiClient.get<BaseUser>(`users/${id}`),

  create: (data: CreateUserRequest) =>
    apiClient.post<BaseUser>('users', data),

  update: (id: string, data: UpdateUserRequest) =>
    apiClient.put<BaseUser>(`users/${id}`, data),

  changePassword: (id: string, data: ChangePasswordRequest) =>
    apiClient.put<{ success: boolean }>(`users/${id}/password`, data),

  adminResetPassword: (id: string, data: AdminResetPasswordRequest) =>
    apiClient.put<{ success: boolean }>(`users/${id}/reset-password`, data),

  completeOnboarding: (userId: string, data: {
    names: ProfileName[];
    idType?: string;
    idNo?: string;
    nationality?: string;
    photoUrl?: string;
    documentsJson?: Record<string, unknown>;
    regionJson?: Record<string, unknown>;
    addressDetail?: string;
    schoolId?: string;
    institutionId?: string;
    customFields?: Record<string, string>;
  }) => apiClient.put(`/users/${userId}/complete-onboarding`, data),

  setupName: (userId: string, data: { displayName: string }) =>
    apiClient.put(`/users/${userId}/setup-name`, data),

  updateDisplayName: (userId: string, data: { displayName: string }) =>
    apiClient.put(`/users/${userId}/display-name`, data),

  delete: (id: string, forceDelete?: boolean) =>
    apiClient.delete<void>(
      `users/${id}${forceDelete ? '?forceDelete=true' : ''}`
    ),

  batchDelete: async (
    data: { userIds?: string[]; filter?: Record<string, string> }
  ): Promise<{ deletedCount: number }> => {
    return apiClient.delete<{ deletedCount: number }>('/users/batch', data);
  },

  getUserActivityLogs: (params: QueryAuditLogsParams) => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.action) searchParams.set('action', params.action);
    if (params.resource) searchParams.set('resource', params.resource);
    if (params.startDate) searchParams.set('startDate', params.startDate);
    if (params.endDate) searchParams.set('endDate', params.endDate);
    const query = searchParams.toString();
    return apiClient.get<AuditLogsResponse>(query ? `users/me/audit-logs?${query}` : 'users/me/audit-logs');
  },
};
